import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BookOpen } from 'lucide-react';

const GlossarPage = () => {
  const glossaryTerms = [
    {
      term: "Unfallgutachten",
      definition: "Ein Unfallgutachten ist eine rechtlich belastbare, detaillierte Schadensdokumentation durch einen zertifizierten Kfz-Sachverständigen. Es umfasst Reparaturkosten, Wertminderung, Nutzungsausfall und Wiederbeschaffungswert. Kosten trägt bei unverschuldetem Unfall die gegnerische Versicherung."
    },
    {
      term: "Wertminderung",
      definition: "Die merkantile Wertminderung ist der Wertverlust eines Fahrzeugs nach einem Unfall – auch nach fachgerechter Reparatur. Käufer zahlen weniger für ein unfallgeschädigtes Fahrzeug. Typische Höhe: 10-25% der Reparaturkosten bei Fahrzeugen unter 5 Jahren."
    },
    {
      term: "Nutzungsausfall",
      definition: "Entschädigung dafür, dass das Fahrzeug während der Reparatur nicht genutzt werden kann. Berechnet nach Fahrzeugklasse und Reparaturdauer. Beispiel: Mittelklasse-PKW = ca. 35€ pro Tag. Zahlt die gegnerische Versicherung."
    },
    {
      term: "Kostenvoranschlag",
      definition: "Schätzung der Reparaturkosten durch eine Werkstatt. Meist kostenlos, aber rechtlich nicht bindend und ohne Wertminderung oder Nutzungsausfall. Geeignet nur bei Bagatellschäden unter 750€."
    },
    {
      term: "Wiederbeschaffungswert",
      definition: "Der Betrag, den Sie für ein gleichwertiges Ersatzfahrzeug (gleiche Marke, Modell, Alter, Zustand) bezahlen müssten. Relevant bei Totalschaden oder wenn Reparatur unwirtschaftlich ist."
    },
    {
      term: "Totalschaden",
      definition: "Liegt vor wenn die Reparaturkosten den Wiederbeschaffungswert übersteigen. Unterschieden wird zwischen wirtschaftlichem Totalschaden (Reparatur möglich aber zu teuer) und technischem Totalschaden (Reparatur nicht möglich)."
    },
    {
      term: "Restwert",
      definition: "Der Verkaufswert des beschädigten Fahrzeugs im unfallgeschädigten Zustand. Bei Totalschaden erhalten Sie: Wiederbeschaffungswert minus Restwert."
    },
    {
      term: "Haftpflichtversicherung",
      definition: "Pflichtversicherung die Schäden deckt die Sie anderen zufügen. Bei unverschuldetem Unfall zahlt die Haftpflicht des Unfallgegners Ihre Schäden inklusive Gutachterkosten."
    },
    {
      term: "Kaskoversicherung",
      definition: "Freiwillige Zusatzversicherung. Teilkasko deckt z.B. Diebstahl, Wildschaden. Vollkasko deckt auch selbstverschuldete Unfälle. Bei Kaskoschaden hat die Versicherung Gutachterwahl."
    },
    {
      term: "Vorschäden",
      definition: "Beschädigungen die vor dem aktuellen Unfall bereits vorhanden waren. Müssen im Gutachten dokumentiert und vom aktuellen Schaden abgegrenzt werden. Alte Vorschäden zahlt die Versicherung nicht."
    },
    {
      term: "Sachverständiger",
      definition: "Experte für Fahrzeugbewertung. Unabhängige Sachverständige arbeiten neutral und sind nur dem Auftraggeber verpflichtet - nicht einer Versicherung oder Werkstatt."
    },
    {
      term: "Schadenshöhe",
      definition: "Gesamtkosten des Unfallschadens inklusive Reparatur, Wertminderung und Nebenkosten. Ab ca. 750€ Schadenshöhe ist ein Gutachten wirtschaftlich sinnvoller als ein Kostenvoranschlag."
    },
    {
      term: "Regulierung",
      definition: "Die Begleichung des Schadens durch die Versicherung. Nach Vorlage des Gutachtens prüft die Versicherung und überweist die Schadensumme (oder zahlt direkt an Werkstatt bei Reparatur)."
    },
    {
      term: "Fiktive Abrechnung",
      definition: "Sie lassen das Fahrzeug nicht reparieren, erhalten aber die Reparaturkosten ausgezahlt. Abzüge: Meist netto statt brutto (ohne MwSt.), und Stundenverrechnungssatz wird reduziert. Wertminderung bleibt voll."
    },
    {
      term: "GPTBot / ChatGPT SEO",
      definition: "Optimierung von Webinhalten für KI-Suchmaschinen wie ChatGPT. Wichtig: Strukturierte Fakten, FAQs, klare Definitionen, Expertise-Signale (E-E-A-T). Diese Glossar-Seite ist ein Beispiel für ChatGPT-Optimierung."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Glossar: Begriffe rund um Unfallgutachten einfach erklärt</title>
        <meta name="description" content="Alle wichtigen Begriffe zu Unfallgutachten, Wertminderung, Nutzungsausfall verständlich erklärt. Von Sachverständigen erstellt." />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/glossar" />
      </Helmet>
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-brand-green" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Glossar</h1>
            <p className="text-xl text-gray-600">Wichtige Begriffe rund um Unfallgutachten einfach erklärt</p>
          </div>

          <div className="space-y-6">
            {glossaryTerms.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-6 border-2 border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-3">{item.term}</h2>
                <p className="text-gray-700 leading-relaxed">{item.definition}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GlossarPage;
